import { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { marked } from 'marked';
import { apiService, type AdminTask, type AdminTaskStats, type AdminTaskDetail } from '../services/api';

interface AdminTasksProps {
  onBack: () => void;
}

const STATUS_MAP: Record<string, { label: string; color: string; bgColor: string }> = {
  queued: { label: '排队中', color: 'text-yellow-700', bgColor: 'bg-yellow-100' },
  processing: { label: '处理中', color: 'text-blue-700', bgColor: 'bg-blue-100' },
  completed: { label: '已完成', color: 'text-green-700', bgColor: 'bg-green-100' },
  failed: { label: '失败', color: 'text-red-700', bgColor: 'bg-red-100' },
};

function formatDate(dateStr: string) {
  const d = new Date(dateStr);
  return d.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function formatFileSize(bytes?: number) {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function AdminTasks({ onBack }: AdminTasksProps) {
  const [tasks, setTasks] = useState<AdminTask[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [statusFilter, setStatusFilter] = useState('');
  const [keyword, setKeyword] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<AdminTaskStats | null>(null);
  const [detailTask, setDetailTask] = useState<AdminTaskDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const result = await apiService.getAdminTasks({
        page,
        pageSize,
        status: statusFilter || undefined,
        keyword: keyword || undefined,
      });
      setTasks(result.tasks);
      setTotal(result.total);
    } catch (err) {
      console.error('获取任务列表失败:', err);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, statusFilter, keyword]);

  const fetchStats = useCallback(async () => {
    try {
      const s = await apiService.getAdminTaskStats();
      setStats(s);
    } catch (err) {
      console.error('获取统计失败:', err);
    }
  }, []);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const totalPages = Math.ceil(total / pageSize);

  const handleSearch = () => {
    setKeyword(searchInput);
    setPage(1);
  };

  const handleStatusFilter = (status: string) => {
    setStatusFilter(status);
    setPage(1);
  };

  const handleViewDetail = async (id: string) => {
    setDetailLoading(true);
    try {
      const detail = await apiService.getAdminTaskDetail(id);
      setDetailTask(detail);
    } catch (err) {
      console.error('获取任务详情失败:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCloseDetail = () => {
    setDetailTask(null);
  };

  return (
    <div className="space-y-6">
      {/* 顶部栏 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="text-slate-600 hover:text-slate-800 font-medium flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            返回
          </button>
          <h2 className="text-xl font-bold text-slate-800">任务管理</h2>
        </div>
        <button
          onClick={() => { fetchTasks(); fetchStats(); }}
          className="px-4 py-2 text-sm bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
        >
          刷新
        </button>
      </div>

      {/* 统计卡片 */}
      {stats && (
        <div className="grid grid-cols-5 gap-4">
          {[
            { label: '全部', value: stats.total, color: 'bg-slate-50 text-slate-700 border-slate-200' },
            { label: '排队中', value: stats.queued, color: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
            { label: '处理中', value: stats.processing, color: 'bg-blue-50 text-blue-700 border-blue-200' },
            { label: '已完成', value: stats.completed, color: 'bg-green-50 text-green-700 border-green-200' },
            { label: '失败', value: stats.failed, color: 'bg-red-50 text-red-700 border-red-200' },
          ].map(item => (
            <div key={item.label} className={`rounded-xl border px-4 py-3 ${item.color}`}>
              <div className="text-xs font-medium opacity-70">{item.label}</div>
              <div className="text-2xl font-bold mt-1">{item.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* 筛选和搜索 */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
          {[
            { label: '全部', value: '' },
            { label: '排队中', value: 'queued' },
            { label: '处理中', value: 'processing' },
            { label: '已完成', value: 'completed' },
            { label: '失败', value: 'failed' },
          ].map(item => (
            <button
              key={item.value}
              onClick={() => handleStatusFilter(item.value)}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                statusFilter === item.value
                  ? 'bg-white text-slate-800 shadow-sm font-medium'
                  : 'text-slate-600 hover:text-slate-800'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 ml-auto">
          <input
            type="text"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            placeholder="搜索 prompt 或 ID..."
            className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300 w-64"
          />
          <button
            onClick={handleSearch}
            className="px-4 py-1.5 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            搜索
          </button>
        </div>
      </div>

      {/* 任务列表表格 */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-4 py-3 text-slate-600 font-medium">ID</th>
                <th className="text-left px-4 py-3 text-slate-600 font-medium">Prompt</th>
                <th className="text-left px-4 py-3 text-slate-600 font-medium">模板</th>
                <th className="text-left px-4 py-3 text-slate-600 font-medium">状态</th>
                <th className="text-left px-4 py-3 text-slate-600 font-medium">生成耗时</th>
                <th className="text-left px-4 py-3 text-slate-600 font-medium">创建时间</th>
                <th className="text-left px-4 py-3 text-slate-600 font-medium">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-slate-500">
                    加载中...
                  </td>
                </tr>
              ) : tasks.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-slate-500">
                    暂无任务
                  </td>
                </tr>
              ) : (
                tasks.map(task => {
                  const statusInfo = STATUS_MAP[task.status] || STATUS_MAP.queued;
                  return (
                    <tr key={task.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                      <td className="px-4 py-3 font-mono text-xs text-slate-500">
                        {task.id.slice(0, 8)}...
                      </td>
                      <td className="px-4 py-3 max-w-xs truncate text-slate-700">
                        {task.prompt}
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {task.template_id || '-'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusInfo.color} ${statusInfo.bgColor}`}>
                          {statusInfo.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-600 text-xs">
                        {task.duration_time || '-'}
                      </td>
                      <td className="px-4 py-3 text-slate-500 text-xs">
                        {formatDate(task.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleViewDetail(task.id)}
                            className="text-blue-600 hover:text-blue-700 text-xs font-medium"
                          >
                            详情
                          </button>
                          {task.status === 'completed' && (
                            <a
                              href={`/api/tasks/${task.id}/download`}
                              className="text-green-600 hover:text-green-700 text-xs font-medium"
                            >
                              下载
                            </a>
                          )}
                          {(task.status === 'completed' || task.status === 'processing') && (
                            <a
                              href={`/api/tasks/${task.id}/preview`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-purple-600 hover:text-purple-700 text-xs font-medium"
                            >
                              预览
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 分页 */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-slate-500">
            共 {total} 条，第 {page}/{totalPages} 页
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(1)}
              disabled={page <= 1}
              className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              首页
            </button>
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              上一页
            </button>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              下一页
            </button>
            <button
              onClick={() => setPage(totalPages)}
              disabled={page >= totalPages}
              className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              末页
            </button>
          </div>
        </div>
      )}

      {/* 详情弹窗 - 使用 Portal 渲染到 body */}
      {detailTask && createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={handleCloseDetail}>
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full mx-4 max-h-[92vh] flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            <div className="shrink-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <h3 className="text-lg font-bold text-slate-800">任务详情</h3>
              <button
                onClick={handleCloseDetail}
                className="text-slate-400 hover:text-slate-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {detailLoading ? (
              <div className="px-6 py-12 text-center text-slate-500">加载中...</div>
            ) : (
              <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1">
                <DetailRow label="ID" value={detailTask.id} mono />
                <DetailRow label="状态" value={STATUS_MAP[detailTask.status]?.label || detailTask.status} />
                <DetailRow label="进度" value={`${detailTask.progress}%`} />
                <DetailRow label="Prompt" value={detailTask.prompt} markdown />
                <DetailRow label="模板" value={detailTask.template_id || '-'} />
                <DetailRow label="转换模式" value={detailTask.conversion_mode || '-'} />
                <DetailRow label="页数" value={detailTask.page_count?.toString() || '-'} />
                <DetailRow label="文件名" value={detailTask.result_file_name || '-'} />
                <DetailRow label="文件大小" value={formatFileSize(detailTask.result_file_size)} />
                <DetailRow label="创建时间" value={formatDate(detailTask.created_at)} />
                <DetailRow label="完成时间" value={detailTask.completed_at ? formatDate(detailTask.completed_at) : '-'} />
                <DetailRow label="更新时间" value={formatDate(detailTask.updated_at)} />
                {detailTask.error && <DetailRow label="错误信息" value={detailTask.error} error />}
                {detailTask.system_prompt && <DetailRow label="System Prompt" value={detailTask.system_prompt} long pre />}
                {detailTask.user_prompt && <DetailRow label="User Prompt" value={detailTask.user_prompt} long markdown />}
                {detailTask.generated_html && (
                  <div className="flex gap-4">
                    <div className="w-28 shrink-0 text-sm text-slate-500 font-medium pt-0.5">生成 HTML</div>
                    <div className="flex-1">
                      <button
                        onClick={() => {
                          const blob = new Blob([detailTask.generated_html!], { type: 'text/html' });
                          const url = URL.createObjectURL(blob);
                          window.open(url, '_blank');
                        }}
                        className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                      >
                        在新窗口打开
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

function DetailRow({ label, value, mono, long, error, markdown, pre }: {
  label: string;
  value: string;
  mono?: boolean;
  long?: boolean;
  error?: boolean;
  markdown?: boolean;
  pre?: boolean;
}) {
  const html = useMemo(() => {
    if (!markdown) return '';
    return marked.parse(value, { async: false }) as string;
  }, [value, markdown]);

  return (
    <div className="flex gap-4">
      <div className="w-28 shrink-0 text-sm text-slate-500 font-medium pt-0.5">{label}</div>
      <div
        className={`text-sm flex-1 break-all ${
          error ? 'text-red-600 bg-red-50 px-3 py-2 rounded-lg' : 'text-slate-700'
        } ${mono ? 'font-mono text-xs' : ''}`}
        style={long ? { maxHeight: '400px', overflow: 'auto', whiteSpace: pre ? 'pre-wrap' : undefined } : pre ? { whiteSpace: 'pre-wrap' } : undefined}
      >
        {markdown ? (
          <div
            className="md-uniform text-sm [&_*]:text-sm [&_*]:my-1 [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-5 [&_ol]:pl-5 [&_strong]:font-semibold [&_em]:italic [&_blockquote]:border-l-2 [&_blockquote]:border-slate-300 [&_blockquote]:pl-3 [&_blockquote]:text-slate-600 [&_code]:bg-slate-100 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_pre]:bg-slate-100 [&_pre]:p-3 [&_pre]:rounded-lg [&_pre]:overflow-x-auto max-w-none"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        ) : (
          value
        )}
      </div>
    </div>
  );
}
